import _ from 'lodash';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { AgentAvailability } from 'models/agent_status';
import analytics from 'scripts/lib/analytics';
import connect, { ExecuteActionProvider } from 'components/lib/connect';
import ErrorBoundary from 'components/common/utilities/error_boundary';
import HotkeysModal from 'components/hotkeys/hotkeys_modal';
import { HotkeysProvider } from 'components/contexts/hotkeys';
import { IsFeatureEnabledProvider } from 'components/contexts/is_feature_enabled';
import ModalContainer from 'components/modals/modal_container';
import Navigation from 'components/root/navigation';
import NotificationsContainer from 'components/notification/notifications_container';
import PageContent from 'components/root/page_content';
import { SpinnerPortal } from 'components/common/full_page_spinner';
import theme from 'scripts/presentation/themes/default';
import Title from 'components/title';
import { ToastPortal } from 'components/common/toast';
import { ToastsContainer } from 'components/toasts';
import UserAwayPage from 'components/user/user_away_page';

export function RootBase({ binding, showAwayPage }) {
  let content = null;
  if (showAwayPage) {
    content = <UserAwayPage />;
  } else {
    content = <RootContent binding={binding} />;
  }

  return (
    <ErrorBoundary renderError={() => <RootError />}>
      <RootProviders>{content}</RootProviders>
    </ErrorBoundary>
  );
}

const Root = connect(mapStateToProps)(RootBase);

function mapStateToProps({ getProvider }) {
  let currentStatus = getProvider('agentStatus').get();
  let pendingStatus = getProvider('agentStatus').getPending();

  const isAgentAway =
    getAvailability(currentStatus) === AgentAvailability.AWAY ||
    getAvailability(pendingStatus) === AgentAvailability.AWAY;
  const isAgentReturning = getAvailability(pendingStatus) === AgentAvailability.BUSY;

  return {
    showAwayPage: isAgentAway && !isAgentReturning,
  };

  function getAvailability(status) {
    return _(status).get('status.availability');
  }
}

export function RootContent({ binding }) {
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);

  const pageClasses = classnames('page-wrapper push', {
    'os-windows': isWindows(),
    'navigation-open': isNavigationOpen,
  });

  return (
    <div className={pageClasses}>
      <Title />
      <NotificationsContainer />
      <Navigation isOpen={isNavigationOpen} setIsOpen={setIsNavigationOpen} />
      <ModalContainer />
      <PageContent binding={binding} />
      <SpinnerPortal />
      <ToastPortal />
      <HotkeysModal />
      <ToastsContainer />
      <div id="modalPortal" />
    </div>
  );
}

function isWindows() {
  return navigator.platform.indexOf('Win') !== -1;
}

function RootError() {
  const onClick = useCallback(() => location.reload(), []);
  useEffect(() => {
    analytics.track('Whoops Screen Shown', {
      componentName: 'root',
    });
  }, [analytics]);

  return (
    <div className="root-errorBoundary">
      <div>Whoops! Something went wrong.</div>
      <div className="root-errorBoundary-continue" onClick={onClick}>
        Click here to reload the page
      </div>
    </div>
  );
}

export function RootProviders({ children }) {
  return (
    <ThemeProvider theme={theme}>
      <HotkeysProvider>
        <ExecuteActionProvider>
          <IsFeatureEnabledProvider>{children}</IsFeatureEnabledProvider>
        </ExecuteActionProvider>
      </HotkeysProvider>
    </ThemeProvider>
  );
}

export default Root;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/root.jsx